import React, { useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { Box, IconButton, Typography } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import dayjs from "dayjs";

const Calendar = ({ onDateSelect, eventMarkers = {}, blockEventDates = false }) => {
    const [currentMonth, setCurrentMonth] = useState(dayjs());
  

    const handlePrevMonth = () => {
        setCurrentMonth(currentMonth.subtract(1, "month"));
    };
    
    const handleNextMonth = () => {
        setCurrentMonth(currentMonth.add(1, "month"));
    };

    const handleDateClick = (date) => {
        const dateString = date.format("YYYY-MM-DD");
        const hasEvent = !!eventMarkers[dateString];
        
        // If blockEventDates is enabled and the date has an event, don't allow selection
        if (blockEventDates && hasEvent) {
            return;
        }
        
        if (onDateSelect) {
            onDateSelect(dateString);
        }
        setCurrentMonth(date);
    };
    
    return(
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center"
          sx={{
            padding: "18px",
            backgroundColor: "#fff",
          }}
        >
          {/* Month Navigation */}
          <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
            <IconButton onClick={handlePrevMonth}>
              <ArrowBackIosIcon />
            </IconButton>
            <Typography variant="h6">
              {currentMonth.format("MMMM YYYY")}
            </Typography>
            <IconButton onClick={handleNextMonth}>
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
  
          {/* Calendar with Borders and Full Grid */}
          <Box
            sx={{
              "& .MuiPickersDay-root": {
                border: "1px solid #ddd", // Light border around each cell
                borderRadius: "0", // Square grid cells
                margin: "0", // No extra spacing
                padding: "8px",
                width: "52px",
                height: "52px",
                backgroundColor: "#fff !important",
                cursor: "pointer", // Added cursor pointer for better UX
              },
              "& .Mui-selected": {
                backgroundColor: "#c49b36 !important", // Gold background for selected date
                color: "white !important",
                fontWeight: "bold",
              },
              "& .MuiPickersDay-root:hover": {
                backgroundColor: "#f4e4a1", // Light gold hover effect
              },
              "& .MuiPickersSlideTransition-root": {
                minHeight: "420px", // Ensures consistent height
              },
              "& .MuiDayCalendar-weekDayLabel": {
                border: "1px solid #ddd", // Border around weekday labels 
                fontWeight: "bold",
                textAlign: "center",
                margin: "0",
                padding: "8px",
                width: "50px",
                height: "52px",
                backgroundColor: "#fff", // Background color for clarity
              },
  
              // Hide the header of the calendar
              "& .MuiPickersCalendarHeader-root": {
                display: "none", // Hide the calendar header
              },
              "& .css-1pv2de5-MuiDayCalendar-weekContainer":{
                margin:"0",
              },
              
            }}
          >
            <DateCalendar
              value={currentMonth}
              onChange={handleDateClick}
              views={["day"]}
              disableHighlightToday
              showDaysOutsideCurrentMonth
              slots={{
                day: (dayProps) => {
                  const { day, outsideCurrentMonth, ...other } = dayProps;
                  const dateStr = day.format("YYYY-MM-DD");
                  const isCurrentMonth = day.month() === currentMonth.month();
                  const marker = eventMarkers[dateStr];
                  const hasEvent = !!marker;
    
                  const isDisabled = blockEventDates && hasEvent;
    
                  return (
                    <Box
                      component={PickersDay}
                      {...other}
                      day={day}
                      outsideCurrentMonth={outsideCurrentMonth}
                      onClick={() => !isDisabled && handleDateClick(day)}
                      sx={{
                        position: "relative",
                        color: isCurrentMonth ? "black" : "#bbb",
                        backgroundColor: isCurrentMonth ? "white" : "#f9f9f9",
                        fontWeight: isCurrentMonth ? "normal" : "normal",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.6 : 1,
                        "&:hover": {
                          backgroundColor: isDisabled ? "transparent" : "#f4e4a1 !important",
                        },
                        
                        // Style for disabled dates with events
                        ...(isDisabled && {
                          backgroundColor: "#f5f5f5 !important",
                          color: "#ccc !important",
                        })
                      }}
                    >
                      {day.date()}
                      
                      {marker && (
                        <Box
                          sx={{
                            width: marker.includes("small") ? 6 : 10,
                            height: marker.includes("small") ? 6 : 10,
                            borderRadius: "50%",
                            background:
                              marker === "purple"
                                ? "#512DA8"
                                : marker === "yellow"
                                ? "#FFC107"
                                : marker === "half-yellow-purple"
                                ? "linear-gradient(90deg, #512DA8 50%, #FFC107 50%)"
                                : marker === "gold"
                                ? "#c49b36"
                                : marker === "small-yellow"
                                ? "#FFC107"
                                : "#512DA8",
                            position: "absolute",
                            bottom: 4,
                            left: "50%",
                            transform: "translateX(-50%)",
                            opacity: isDisabled ? 0.4 : 1, // Make markers less visible on disabled dates
                          }}
                        />
                      )}
                    </Box>
                  );
                }
              }}
            />
          </Box>
        </Box>
      </LocalizationProvider>
    )
}

export default Calendar;